<template>
  <o-dialog
    ref="dialog"
    :title="$t('components.schedule.actions.editBreak.title')"
    :info="$t('components.schedule.actions.editBreak.info')"
    :options="[
      { text: $t('general.cancel'), type: 'cancel', callback: cancel },
      { text: $t('components.schedule.actions.editBreak.delete'), type: 'confirm', variant: 'danger', callback: remove },
      { text: $t('components.schedule.actions.editBreak.save'), type: 'confirm', callback: confirm }
    ]"
    :portal="portal"
  >
    <div v-if="employeeBreak" class="flex flex-col space-y-3 text-left mt-3">
      <o-field-group :label="$t('general.employee')" label-class="text-sm text-gray-600">
        <div class="flex items-center h-full pr-2 text-sm">
          <o-avatar :items="[employeeBreak.employee]" class="hidden md:flex pr-2" />
          <p class="block text-gray-800">
            {{ employeeBreak.employee.displayName }}
          </p>
        </div>
      </o-field-group>

      <o-field-group :label="$t('general.date')" label-class="text-sm text-gray-600">
        <p class="block text-gray-800 text-sm">
          {{ $date.format(employeeBreak.date.date, $date.presets.long) }}
        </p>
      </o-field-group>

      <o-field-group :label="$t('general.time')" label-class="text-sm text-gray-600">
        <div class="grid grid-cols-2 gap-2">
          <div>
            <label class="text-xs text-gray-500 dark:text-gray-400">{{ $t('general.startTime') }}</label>
            <time-field v-model="employeeBreak.time" :augmenter="Augmenters.minutes" />
          </div>
          <div>
            <label class="text-xs text-gray-500 dark:text-gray-400">{{ $t('general.duration') }}</label>
            <UInput type="number" v-model="employeeBreak.duration" :min="0" :max="500" />
          </div>
        </div>
      </o-field-group>

      <o-field-group :label="$t('general.label')" label-class="text-sm text-gray-600 dark:text-gray-300">
        <UInput v-model="employeeBreak.label" />
      </o-field-group>
    </div>
  </o-dialog>
</template>

<script setup lang="ts">
import cloneDeep from 'lodash/cloneDeep'

import { augmenters as Augmenters } from '@/composables/useFieldAugmenters'

import {
  OAvatar,
} from '@/components/elements'

import {
  TimeField,
  NumberField,
  DateField,
  TextField,
} from '@/components/fields'

interface Props {
  portal?: string
}

withDefaults(defineProps<Props>(), {
  portal: undefined,
})

const emit = defineEmits<{
  'update:loading': [value: boolean]
  'cancel': []
  'success': []
}>()

const { $api, $notification, $bus, $date, $i18n } = useNuxtApp()

const dialog = ref<any>(null)

const employeeBreak = ref<any>(null)

function open(block: any) {
  employeeBreak.value = cloneDeep(block?.employeeBreak)

  dialog.value.open()
}

async function confirm() {
  emit('update:loading', true)

  return await editBreak()
}

async function remove() {
  try {
    const eb = employeeBreak.value
    const date = $date.timestamp($date.getStartOfDayUTC(eb.date.date))

    const success = await $api.roster.deleteBreak(eb, date)

    if (success) {
      $notification.toast({
        title: $i18n.t('components.schedule.actions.editBreak.removeSuccess'),
        text: $i18n.t('components.schedule.actions.editBreak.removeSuccessInfo', { name: eb.employee.displayName }),
        variant: 'success',
      })

      $bus.emit.deleted('EmployeeBreak', eb)

      emit('success')
      return true
    }
  } catch (e: any) {
    $notification.error({ errors: e?.errors })
    console.log(e)
  }
}

function cancel() {
  emit('cancel')
  return true
}

async function editBreak() {
  const eb = employeeBreak.value
  const { date, employee, time, duration, label } = eb
  const fromDate = $date.timestamp($date.getStartOfDayUTC(date.date))
  const toDate = $date.timestamp($date.getStartOfDayUTC(date.date))

  try {
    const success = await $api.roster.moveBreak(eb, fromDate, toDate, employee, time, duration, label)

    emit('update:loading', false)

    if (success) {
      $notification.toast({
        title: $i18n.t('schedule.updatedBreak'),
        text: $i18n.t('components.schedule.actions.editBreak.editSuccessInfo', { date: $date.format(date.date, $date.presets.long), time: $date.minutes(time), name: employee.displayName }),
        variant: 'success',
      })

      $bus.emit.updated('EmployeeBreak', eb)

      emit('success')
      return true
    }
  } catch (e: any) {
    $notification.error({ errors: e?.errors })
    console.log(e)
  }
}

defineExpose({ open })
</script>

<style lang="scss">
.roster-cell-container > div {
  height: 100%
}
</style>
